import httpClient from '../../services/httpClient';

export interface Profile {
  username: string;
  email: string;
  joined: string;
  status: 'active' | 'inactive' | 'suspended';
  totalOrders: number;
  avatar?: string;
  adminRole?: boolean;
  telegramLinked?: boolean;
}

// Shape of the object Telegram Login Widget passes to its data-onauth callback.
export interface TelegramWidgetUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

export interface GetProfileResponse {
  profile: Profile;
}

export interface ProfileError {
  message: string;
  code?: string;
  status?: number;
}

export const profileService = {
  // Get user profile
  getProfile: async (): Promise<GetProfileResponse> => {
    try {
      const response = await httpClient.get('api/Users/profile');
      const data = response.data;
      const displayName = data.userName || [data.firstName, data.lastName].filter(Boolean).join(' ') || 'User';
      return {
        profile: {
          username: displayName,
          email: data.email || '',
          joined: '',
          status: 'active' as const,
          totalOrders: data.orders?.length ?? 0,
          avatar: data.photoUrl,
          adminRole: data.adminRole === true,
          telegramLinked: data.telegramLinked === true
        }
      };
    } catch (error) {
      console.error('Error fetching profile:', error);
      
      if (error instanceof Error) {
        throw {
          message: error.message,
          code: 'PROFILE_FETCH_ERROR',
          status: error.message.includes('not found') ? 404 : 500
        } as ProfileError;
      }
      
      throw {
        message: 'Failed to load profile. Please try again.',
        code: 'UNKNOWN_ERROR',
        status: 500
      } as ProfileError;
    }
  },

  // Link a Telegram identity (from the Login Widget) to the current account.
  // On a 409 the caller inspects error.response.data.status ("confirm_required" | "conflict").
  linkTelegram: async (user: TelegramWidgetUser, confirm = false): Promise<{ status: string; orders?: number }> => {
    const { data } = await httpClient.post('/api/Auth/link/telegram', {
      id: user.id,
      firstName: user.first_name,
      lastName: user.last_name,
      username: user.username,
      photoUrl: user.photo_url,
      authDate: user.auth_date,
      hash: user.hash,
      confirm,
    });
    return data;
  }
};